/**
 * ViewEmailCommunications - Email send log admin page (Product Owner only)
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Pagination, Modal } from "@/ui/atoms";
import { cn } from "@/utils/utils";
import type { TApiEmailLogEntry, TApiEmailLogStatus } from "@/apis/proda_finance/types";

const STATUS_COLORS: Record<string, string> = {
    sent: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
    pending: "bg-yellow-100 text-yellow-700",
};

const STATUS_LABELS: Record<string, string> = {
    sent: "Sent",
    failed: "Failed",
    pending: "Pending",
};

const ALL_STATUSES: TApiEmailLogStatus[] = ["sent", "failed", "pending"];

/** Turn a notification_type slug into a readable label. */
const typeLabel = (value: string): string =>
    value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const StatusPill = ({ status }: { status: string }) => (
    <span
        className={cn(
            "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
            STATUS_COLORS[status] || "bg-gray-100 text-gray-700",
        )}
    >
        {STATUS_LABELS[status] || status}
    </span>
);

/** One label/value row in the detail modal. */
const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="grid grid-cols-[140px_1fr] gap-3">
        <dt className="text-xs font-semibold text-muted-foreground pt-0.5">{label}</dt>
        <dd className="text-sm break-words min-w-0">{children}</dd>
    </div>
);

export type TViewEmailCommunicationsParams = {
    useCases: () => {
        loadEmailLogs: (params: {
            page: number;
            pageSize: number;
            status?: TApiEmailLogStatus;
        }) => Promise<{
            items: TApiEmailLogEntry[];
            total: number;
        }>;
    };
};

export type TViewEmailCommunicationsProps = Record<string, never>;

export const createViewEmailCommunications = ({ useCases }: TViewEmailCommunicationsParams) => {
    return function ViewEmailCommunications() {
        const { loadEmailLogs } = useCases();
        const [searchParams, setSearchParams] = useSearchParams();

        const [entries, setEntries] = useState<TApiEmailLogEntry[]>([]);
        const [total, setTotal] = useState(0);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);
        const [selectedEntry, setSelectedEntry] = useState<TApiEmailLogEntry | null>(null);

        const page = parseInt(searchParams.get("page") || "1", 10);
        const pageSize = 20;
        const statusFilter = (searchParams.get("status") as TApiEmailLogStatus | null) ?? undefined;

        const fetchEmailLogs = useCallback(async () => {
            setIsLoading(true);
            setError(null);
            try {
                const result = await loadEmailLogs({ page, pageSize, status: statusFilter });
                setEntries(result.items);
                setTotal(result.total);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load email log");
            } finally {
                setIsLoading(false);
            }
        }, [loadEmailLogs, page, pageSize, statusFilter]);

        useEffect(() => {
            fetchEmailLogs();
        }, [fetchEmailLogs]);

        return (
            <div className="space-y-6" data-testid="email-communications-view">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-semibold">Email Communications</h1>
                </div>
                <p className="text-sm text-muted-foreground">
                    Every email sent by PRODA Finance for this company, newest first. Failed and
                    pending attempts are included.
                </p>

                {/* Status filter */}
                <div className="flex items-center gap-1 rounded-lg border bg-muted/40 p-1 w-fit">
                    {[
                        { value: undefined, label: "All" },
                        ...ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
                    ].map((item) => (
                        <button
                            key={item.value ?? "all"}
                            onClick={() => {
                                const params = new URLSearchParams(searchParams);
                                if (item.value) {
                                    params.set("status", item.value);
                                } else {
                                    params.delete("status");
                                }
                                params.set("page", "1");
                                setSearchParams(params);
                            }}
                            className={cn(
                                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                statusFilter === item.value || (!statusFilter && !item.value)
                                    ? "bg-background text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground",
                            )}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                        {error}
                    </div>
                )}

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    </div>
                ) : entries.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        No emails logged yet.
                    </div>
                ) : (
                    <>
                        <div className="rounded-md border">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="px-4 py-3 text-left font-medium w-40">
                                            Sent At
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium">Type</th>
                                        <th className="px-4 py-3 text-left font-medium w-24">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium">To</th>
                                        <th className="px-4 py-3 text-left font-medium w-16">CC</th>
                                        <th className="px-4 py-3 text-left font-medium">Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((entry) => (
                                        <tr
                                            key={entry.id}
                                            className="border-b cursor-pointer hover:bg-muted/30 transition-colors even:bg-muted/40"
                                            onClick={() => setSelectedEntry(entry)}
                                        >
                                            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                                                {new Date(entry.created_at).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3">
                                                {typeLabel(entry.notification_type)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusPill status={entry.status} />
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground max-w-72 truncate">
                                                {entry.to_recipients.join(", ")}
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">
                                                {entry.cc_recipients.length || "-"}
                                            </td>
                                            <td className="px-4 py-3 text-red-700 max-w-64 truncate">
                                                {entry.error_message || ""}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            total={total}
                            onPageChange={(newPage) => {
                                const params = new URLSearchParams(searchParams);
                                params.set("page", String(newPage));
                                setSearchParams(params);
                            }}
                        />
                    </>
                )}

                {/* Detail modal */}
                {selectedEntry && (
                    <Modal
                        open={true}
                        onOpenChange={(open) => {
                            if (!open) setSelectedEntry(null);
                        }}
                        title={typeLabel(selectedEntry.notification_type)}
                    >
                        <div className="space-y-4 max-h-[60vh] overflow-auto">
                            <dl className="space-y-2 border-b pb-4">
                                <DetailRow label="Sent At">
                                    {new Date(selectedEntry.created_at).toLocaleString()}
                                </DetailRow>
                                <DetailRow label="Status">
                                    <StatusPill status={selectedEntry.status} />
                                </DetailRow>
                                <DetailRow label="From">{selectedEntry.from_email}</DetailRow>
                                <DetailRow label="To">
                                    {selectedEntry.to_recipients.join(", ")}
                                </DetailRow>
                                {selectedEntry.cc_recipients.length > 0 && (
                                    <DetailRow label="CC">
                                        {selectedEntry.cc_recipients.join(", ")}
                                    </DetailRow>
                                )}
                                {selectedEntry.bcc_recipients.length > 0 && (
                                    <DetailRow label="BCC">
                                        {selectedEntry.bcc_recipients.join(", ")}
                                    </DetailRow>
                                )}
                                {selectedEntry.recipient_source && (
                                    <DetailRow label="Recipient Source">
                                        {typeLabel(selectedEntry.recipient_source)}
                                    </DetailRow>
                                )}
                                <DetailRow label="Template">
                                    <span className="font-mono text-xs break-all">
                                        {selectedEntry.template_id}
                                    </span>
                                </DetailRow>
                                <DetailRow label="Related Entity">
                                    <span className="font-mono text-xs break-all">
                                        {selectedEntry.entity_id}
                                    </span>
                                </DetailRow>
                                {selectedEntry.error_message && (
                                    <DetailRow label="Error">
                                        <span className="text-red-700">
                                            {selectedEntry.error_message}
                                        </span>
                                    </DetailRow>
                                )}
                            </dl>
                            <div>
                                <h3 className="text-xs font-semibold text-muted-foreground mb-2">
                                    Template Data
                                </h3>
                                <pre className="rounded-md bg-muted/50 p-3 text-xs overflow-auto">
                                    {JSON.stringify(selectedEntry.payload, null, 2)}
                                </pre>
                            </div>
                        </div>
                    </Modal>
                )}
            </div>
        );
    };
};
